import { createContext, createElement } from 'react'

import type { IPlansInitialValue } from '@/contexts/PlanContext/type'

import { useDuration } from '@/contexts/PlanContext/hooks/duration'
import { useSiteSpecificContext } from '@/contexts/SiteSpecificContext'
import { ProductsSubAppName } from '@/contexts/SiteSpecificContext/types'
import { usePayStep } from '@/contexts/PlanContext/hooks/payStep'
import { usePlanProductList } from '@/contexts/PlanContext/hooks/planProductList'
import { useMaxOutlets } from '@/contexts/PlanContext/hooks/maxOutlets'


import { useContext } from 'react';
function usePlans(initialValue: IPlansInitialValue) {
  const {
    productList,
    effectiveUserGroup,
    onCurrentDurationChange,
    appName = ProductsSubAppName.App1,
    onChangeStep,
    isModal,
    isActive = true,
    size = 'middle',
    isAnonymousPayment = true,
  } = initialValue

  const { plan } = useSiteSpecificContext()

  const durationInfo = useDuration({
    plan,
    onCurrentDurationChange,
  })

  const payStepInfo = usePayStep({
    plan,
    onChangeStep,
    appName,
    isModal,
  })

  const planProductListInfo = usePlanProductList({
    initProductsList: productList,
    appName,
    effectiveUserGroup,
  })

  const { allProductsList, allProductsListMap } = planProductListInfo

  const maxOutletsInfo = useMaxOutlets({})

  return {
    durationInfo,
    payStepInfo,
    allProductsList,
    allProductsListMap,
    isActive,
    plan,
    maxOutletsInfo,
    isModal,
    appName,
    size,
    isAnonymousPayment,
    planProductListInfo,
  }
}

export type TUsePlan = ReturnType<typeof usePlans>

const Context = createContext<TUsePlan>(null!)

export function PlansProvider({
  children,
  initialValue,
}: {
  children: React.ReactNode
  initialValue: IPlansInitialValue
}) {
  const value = usePlans(initialValue)

  return createElement(
    Context.Provider,
    {
      value,
    },
    children,
  )
}

export function usePlansContext() {
  const context = useContext(Context)
  if (!context) {
    throw new Error('usePlansContext must be used within a PlansProvider')
  }
  return context
}
